import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Button, Divider,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import PatientCodeBarcodes from 'components/PatientCodeBarcodes';
import { resolveCoupleSpouseCodes } from 'utils/printBarcodes';

const FOLLICLE_SIZES = [
  '<10', '10', '10.5', '11', '11.5', '12', '12.5', '13', '13.5', '14',
  '14.5', '15', '15.5', '16', '16.5', '17', '17.5', '18', '18.5', '19',
  '19.5', '20', '20.5', '21', '>21', 'Cyst',
];

function recordsToGrid(records) {
  const grid = {};
  FOLLICLE_SIZES.forEach(s => { grid[s] = { right: 0, left: 0 }; });
  (records || []).forEach(r => {
    if (grid[r.follicle_size]) grid[r.follicle_size][r.side] = r.count;
  });
  return grid;
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Print-only styles injected into a <style> tag ─────────────────────────────
const PRINT_STYLES = `
  @media print {
    body * { visibility: hidden !important; }
    #folliculometry-print-area,
    #folliculometry-print-area * { visibility: visible !important; }
    #folliculometry-print-area {
      position: fixed;
      inset: 0;
      overflow: visible;
      z-index: 99999;
      background: white;
      padding: 18px 22px;
    }
    @page { margin: 15mm 12mm; }
  }
`;

// ── Individual day section ─────────────────────────────────────────────────────
function PrintDay({ day, index }) {
  const grid = recordsToGrid(day.records || []);
  const activeSizes = FOLLICLE_SIZES.filter(s => (grid[s]?.right || 0) + (grid[s]?.left || 0) > 0);

  const paramsByType = {};
  (day.params || []).forEach(p => {
    if (!paramsByType[p.param_type]) paramsByType[p.param_type] = [];
    paramsByType[p.param_type].push(p);
  });

  const s = {
    row: { display: 'flex', gap: 4, marginBottom: 2 },
    label: { fontWeight: 700, fontSize: 10, color: '#333', textTransform: 'uppercase', letterSpacing: '0.4px', minWidth: 90 },
    value: { fontSize: 11, color: '#000' },
    th: { border: '1px solid #999', padding: '3px 7px', fontWeight: 700, fontSize: 10, backgroundColor: '#e8e8e8', textAlign: 'center' },
    td: { border: '1px solid #bbb', padding: '3px 7px', fontSize: 11, textAlign: 'center' },
    tdLeft: { border: '1px solid #bbb', padding: '3px 7px', fontSize: 11, textAlign: 'left', fontWeight: 600 },
  };

  return (
    <Box sx={{
      border: '1px solid #ccc',
      borderRadius: 1,
      mb: 2,
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
      overflow: 'hidden',
    }}>
      {/* Day header */}
      <Box sx={{ backgroundColor: '#e8e8e8', px: 1.5, py: 0.75, display: 'flex', gap: 3, flexWrap: 'wrap', borderBottom: '1px solid #ccc' }}>
        <Typography sx={{ fontWeight: 800, fontSize: 12 }}>Day {index + 1} — {fmt(day.date)}</Typography>
        {day.cycle_day && <Typography sx={{ fontSize: 11 }}>Cycle Day: <b>{day.cycle_day}</b></Typography>}
        {day.cycle_type && <Typography sx={{ fontSize: 11 }}>Cycle Type: <b>{day.cycle_type}</b></Typography>}
        {day.endometrium && <Typography sx={{ fontSize: 11 }}>Endometrium: <b>{day.endometrium} mm</b></Typography>}
        {day.agonist_value && <Typography sx={{ fontSize: 11 }}>Agonist: <b>{day.agonist_value}</b></Typography>}
        {day.antagonist_value && <Typography sx={{ fontSize: 11 }}>Antagonist: <b>{day.antagonist_value}</b></Typography>}
      </Box>

      <Box sx={{ px: 1.5, py: 1 }}>
        {/* Follicle counts */}
        {activeSizes.length > 0 && (
          <Box mb={1}>
            <Typography sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5, color: '#333' }}>
              Follicle Counts
            </Typography>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={s.th}>Size (mm)</th>
                  <th style={s.th}>Right</th>
                  <th style={s.th}>Left</th>
                  <th style={s.th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {activeSizes.map(size => {
                  const r = grid[size]?.right || 0;
                  const l = grid[size]?.left || 0;
                  return (
                    <tr key={size}>
                      <td style={s.tdLeft}>{size}</td>
                      <td style={s.td}>{r}</td>
                      <td style={s.td}>{l}</td>
                      <td style={{ ...s.td, fontWeight: 700 }}>{r + l}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}

        {/* SERM / DRUGS / TRIGGER */}
        {['SERM', 'DRUGS', 'TRIGGER'].map(type => {
          const ps = paramsByType[type];
          if (!ps || ps.length === 0) return null;
          return (
            <Box key={type} mb={0.75}>
              <Typography sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.3, color: '#333' }}>
                {type}
              </Typography>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={s.th}>Name</th>
                    <th style={s.th}>Value / Dose</th>
                  </tr>
                </thead>
                <tbody>
                  {ps.map((p, i) => (
                    <tr key={i}>
                      <td style={s.tdLeft}>{p.param_name || '—'}</td>
                      <td style={s.td}>{p.param_value || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          );
        })}

        {/* Drugs */}
        {(day.drugs || []).length > 0 && (
          <Box mb={0.75}>
            <Typography sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.3, color: '#333' }}>
              Drugs
            </Typography>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={s.th}>Type</th>
                  <th style={s.th}>Drug Name</th>
                  <th style={s.th}>Dose</th>
                  <th style={s.th}>Route</th>
                  <th style={s.th}>Days</th>
                  <th style={s.th}>Time</th>
                </tr>
              </thead>
              <tbody>
                {day.drugs.map((d, i) => (
                  <tr key={i}>
                    <td style={s.td}>{d.drug_type || '—'}</td>
                    <td style={s.tdLeft}>{d.drug_name || '—'}</td>
                    <td style={s.td}>{d.dose || '—'}</td>
                    <td style={s.td}>{d.route || '—'}</td>
                    <td style={s.td}>{d.days || '—'}</td>
                    <td style={s.td}>{(d.times || []).length > 0 ? d.times.join(', ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        {/* Actions & Comments */}
        {(day.actions || day.comments) && (
          <Box display="flex" gap={2} mt={0.5}>
            {day.actions && (
              <Box flex={1}>
                <Typography sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#333', mb: 0.3 }}>Actions</Typography>
                <Typography sx={{ fontSize: 11, whiteSpace: 'pre-wrap', color: '#000' }}>{day.actions}</Typography>
              </Box>
            )}
            {day.comments && (
              <Box flex={1}>
                <Typography sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#333', mb: 0.3 }}>Comments</Typography>
                <Typography sx={{ fontSize: 11, whiteSpace: 'pre-wrap', color: '#000' }}>{day.comments}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

PrintDay.propTypes = {
  day: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

// ── Main export ────────────────────────────────────────────────────────────────
export default function FolliculometryPrint({ open, onClose, folliculometry, days, patient, procedure }) {
  const printRef = useRef(null);

  const handlePrint = () => {
    const styleEl = document.createElement('style');
    styleEl.id = '__folliculo_print_styles__';
    styleEl.innerHTML = PRINT_STYLES;
    document.head.appendChild(styleEl);
    window.print();
    setTimeout(() => {
      const el = document.getElementById('__folliculo_print_styles__');
      if (el) el.remove();
    }, 1000);
  };

  if (!folliculometry) return null;

  const sortedDays = [...(days || [])].sort((a, b) => {
    if (a.date && b.date) return new Date(a.date) - new Date(b.date);
    if (a.cycle_day && b.cycle_day) return Number(a.cycle_day) - Number(b.cycle_day);
    return (a.id || 0) - (b.id || 0);
  });

  const patientName = patient?.full_name || patient?.name || 'Patient';
  const procedureCode = procedure?.external_code || `#${folliculometry.irm_ivf_procedures_id || ''}`;
  const { coupleCode, spouseCode } = resolveCoupleSpouseCodes(patient);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}
    >
      {/* Dialog toolbar — hidden on print */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, py: 1.5, borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa',
        '@media print': { display: 'none' },
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#344767' }}>
          Print Preview — Folliculometry #{folliculometry.id}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
            onClick={handlePrint}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px', backgroundColor: '#212529 !important', color: 'white !important', px: 2 }}
          >
            Print
          </Button>
          <IconButton size="small" onClick={onClose} sx={{ color: '#6c757d' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        {/* ── Printable area ── */}
        <Box
          id="folliculometry-print-area"
          ref={printRef}
          sx={{
            p: 3,
            backgroundColor: 'white',
            color: '#000',
            fontFamily: '"Times New Roman", Times, serif',
            minWidth: 0,
          }}
        >
          {/* Report header */}
          <Box sx={{ textAlign: 'center', mb: 2, borderBottom: '2px solid #000', pb: 1.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '0.5px', color: '#000' }}>
              IVF FOLLICULOMETRY REPORT
            </Typography>
            <Typography sx={{ fontSize: 12, mt: 0.5, color: '#333' }}>
              {patientName} &nbsp;·&nbsp; Procedure: {procedureCode}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <PatientCodeBarcodes mono patientCode={patient?.external_code} coupleCode={coupleCode} spouseCode={spouseCode} documentCode={procedure?.external_code} documentLabel="Procedure No" />
            </Box>
          </Box>

          {/* Session meta row */}
          <Box sx={{ display: 'flex', gap: 4, mb: 1.5, flexWrap: 'wrap', border: '1px solid #ccc', p: 1, borderRadius: 1, backgroundColor: '#f5f5f5' }}>
            <Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#555' }}>Session ID</Typography>
              <Typography sx={{ fontSize: 12, color: '#000' }}>#{folliculometry.id}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#555' }}>Status</Typography>
              <Typography sx={{ fontSize: 12, color: '#000', fontWeight: 700 }}>{folliculometry.status}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#555' }}>Started</Typography>
              <Typography sx={{ fontSize: 12, color: '#000' }}>{fmt(folliculometry.created_at)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#555' }}>Total Days</Typography>
              <Typography sx={{ fontSize: 12, color: '#000' }}>{sortedDays.length}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#555' }}>Printed On</Typography>
              <Typography sx={{ fontSize: 12, color: '#000' }}>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2, borderColor: '#aaa' }} />

          {/* Day sections */}
          {sortedDays.length === 0 ? (
            <Typography sx={{ textAlign: 'center', fontSize: 13, color: '#555', py: 3 }}>
              No days recorded for this session.
            </Typography>
          ) : (
            sortedDays.map((day, i) => (
              <PrintDay key={day.id} day={day} index={i} />
            ))
          )}

          {/* Footer */}
          <Box sx={{ mt: 3, pt: 1.5, borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 9, color: '#666' }}>
              Generated by ICRM · {new Date().toLocaleString('en-GB')}
            </Typography>
            <Typography sx={{ fontSize: 9, color: '#666' }}>
              Confidential — For medical use only
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

FolliculometryPrint.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folliculometry: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    created_at: PropTypes.string,
    irm_ivf_procedures_id: PropTypes.number,
  }),
  days: PropTypes.array,
  patient: PropTypes.object,
  procedure: PropTypes.object,
};
